import * as Sentry from "@sentry/react"
import { BrowserTracing } from "@sentry/tracing"
import { advertiserRPO } from "advertiser/repository"
import { advertiserSVC } from "advertiser/service"
import { advertiserDataLoader } from "advertiser/tools/start"
import { agencySVC } from "agency/service"
import { agencyDataLoader } from "agency/tools/start"
import { App } from "app/components/App"
import { appEventModule } from "app/event"
import { languageSVC } from "app/language/service"
import { startAppListeners } from "app/listeners"
import { APP_EVENTS, IAppDataLoader, IAppInitParams, NOT_SAVED_ID } from "app/model"
import { appActionCreator } from "app/store/actions"
import { getStore } from "app/store/store"
import { audienceDataLoader } from "audience/tools/start"
import { campaignRPO } from "campaign/repository"
import { campaignSVC } from "campaign/service"
import { statusDataLoader, typesDataLoader } from "campaign/tools/start"
import { currencyDataLoader } from "currency/tools/start"
import { dspDataLoader } from "dsp/tools/start"
import { envDataLoader } from "environments/tools/start"
import { mapSVC } from "form/map/service"
import { formatDataLoader } from "format/tools/start"
import { mediaDataLoader } from "media/tools/start"
import { messageSVC } from "message/service"
import { orderRPO } from "order/repository"
import { orderSVC } from "order/service"
import { orderDataLoader } from "order/tools/start"
import { pageSVC } from "page/service"
import React from "react"
import ReactDOM from "react-dom"
import { weatherLoader } from "schedule/tools/start"
import { sspDataLoader } from "ssp/tools/start"
import { loadUIConfig } from "ui/configuration/tools/common"
import { uiSVC } from "ui/service"
import { getOrCreateAppContainer } from "ui/tools/container"
import { tagDataLoader } from "user-tag/tools/start"
import { IUserInfo } from "user/model"
import { userRPO } from "user/repository"
import { userSVC } from "user/service"
import { checkTokenInLocalStorage } from "user/tools/common"
import { initializeAPIBaseUrl } from "./api"
import { notifyWindowResize } from "./common"

export async function startApp() {
  if (NODE_ENV !== "development") {
    startSentry() // production error reporting
    startHotjar() // user behavior reporting
  }
  initializeAPIBaseUrl()
  await loadUIConfig()
  removeSkeletton()
  startAppListeners()
  checkTokenInLocalStorage()
    .catch(error => console.error("Could not init session from token", error))
    .finally(() => {
      ReactDOM.render(<App />, getOrCreateAppContainer())
      appEventModule.notify(APP_EVENTS.APP_READY)
    })
  window.onresize = () => notifyWindowResize()
  if (IS_NOT_PROD) {
    const win: any = window
    win.App = {
      Message: messageSVC,
      Page: pageSVC,
      User: userSVC,
      UI: uiSVC,
      Campaign: campaignSVC,
      Agency: agencySVC,
      Language: languageSVC,
      Order: orderSVC,
      Advertiser: advertiserSVC,
      Map: mapSVC
    }
    win.Test = {
      userRPO,
      campaignRPO,
      orderRPO,
      advertiserRPO
    }
  }
}

export async function loadAppData(userInfo: IUserInfo): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const params: IAppInitParams = {
      userInfo,
      statuses: [],
      types: [],
      currencies: [],
      environments: {
        list: [],
        tree: [],
        environmentById: {}
      },
      ssps: [],
      audiences: [],
      advertisers: [],
      advertiserDomains: [],
      agencies: [],
      orders: [],
      operators: [],
      mediaTypes: [],
      userTags: [],
      formats: [],
      weathers: [],
      dsps: []
    }
    let processedCount = 0
    let toProcessCount = 0
    getStore().dispatch(appActionCreator.startInitData())
    const onFinish = (error?: string) => {
      if (error) {
        getStore().dispatch(appActionCreator.initDataError())
        return reject(`Unexpected error occured while loading campaign data: ${error}`)
      }
      if (++processedCount === toProcessCount) {
        getStore().dispatch(appActionCreator.initData(params))
        resolve()
      }
    }
    const allDataLoaders: IAppDataLoader[] = [
      statusDataLoader(params, onFinish),
      typesDataLoader(params, onFinish),
      currencyDataLoader(params, onFinish),
      envDataLoader(params, onFinish),
      sspDataLoader(params, onFinish),
      agencyDataLoader(params, onFinish),
      orderDataLoader(params, onFinish),
      advertiserDataLoader(params, onFinish),
      audienceDataLoader(params, onFinish),
      mediaDataLoader(params, onFinish),
      tagDataLoader(params, onFinish),
      formatDataLoader(params, onFinish),
      weatherLoader(params, onFinish),
      dspDataLoader(params, onFinish)
    ]
    toProcessCount = allDataLoaders.length
  })
}

export const stopInitData = () => getStore().dispatch(appActionCreator.stopInitData())

function removeSkeletton(): void {
  const skeletton = window.document.getElementById("app-skeletton")
  if (skeletton) {
    window.document.body.removeChild(skeletton)
  }
}

function startSentry(): void {
  Sentry.init({
    environment: NODE_ENV,
    dsn: "https://82ca52bc9e7d4781824716d65bbcf9c1@o4504531953385472.ingest.sentry.io/4504531957841920",
    integrations: [new BrowserTracing()],
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    tracesSampleRate: NODE_ENV === "production" ? 0.3 : 1
  })
}

function startHotjar(): void {
  if (HOTJAR_SITE_ID === NOT_SAVED_ID) {
    return
  }
  uglyHotjarFn(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=", undefined, undefined)
}

function uglyHotjarFn(h: any, o: any, t: any, j: any, a?: any, r?: any) {
  h.hj =
    h.hj ||
    function (...args: any[]) {
      const array = (h.hj.q = h.hj.q || [])
      array.push(args)
    }
  h._hjSettings = { hjid: HOTJAR_SITE_ID, hjsv: 6 }
  a = o.getElementsByTagName("head")[0]
  r = o.createElement("script")
  r.async = 1
  r.src = `${t}${h._hjSettings.hjid}${j}${h._hjSettings.hjsv}`
  a.appendChild(r)
}
